import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider
} from '@mui/material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CompressIcon from '@mui/icons-material/Compress';

interface GallerySection {
  subtitle: string;
  title: string;
  description: string;
  tiles: GalleryTile[];
}

interface GalleryTile {
  title: string;
  link: string;
  chip: string;
  type: 'photo' | 'video';
  imageUrl?: string;
  youtubeId?: string;
}

interface ResizeSettings {
  maxWidth: number;
  maxHeight: number;
  quality: number;
}

const GallerySectionManager = () => {
  const [content, setContent] = useState<GallerySection>({
    subtitle: 'GALLERY OVERVIEW',
    title: 'Explore Photos & Videos',
    description: 'A glimpse of temple moments — festivals, premises, events and serene views.',
    tiles: [
      {
        title: 'Photos',
        link: '/gallery/photos',
        chip: 'Curated',
        type: 'photo',
        imageUrl: '/assets/header_god_image.png',
      },
      {
        title: 'Videos',
        link: '/gallery/videos',
        chip: 'Highlights',
        type: 'video',
        youtubeId: 'bFHz3KocQc0',
      },
      {
        title: 'Events',
        link: '/gallery/events',
        chip: 'Recent',
        type: 'photo',
        imageUrl: '/assets/slide_3.png',
      },
      {
        title: 'Temple Tour',
        link: '/gallery/videos',
        chip: 'Tour',
        type: 'video',
        youtubeId: 'bFHz3KocQc0',
      },
    ],
  });
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Resize settings
  const [resizeDialogOpen, setResizeDialogOpen] = useState(false);
  const [resizeTileIndex, setResizeTileIndex] = useState<number | null>(null);
  const [resizeSettings, setResizeSettings] = useState<ResizeSettings>({
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 92
  });
  const [resizing, setResizing] = useState(false);
  const [originalSize, setOriginalSize] = useState<number>(0);
  const [compressedSize, setCompressedSize] = useState<number>(0);

  // Utility function to resize/compress image
  const resizeImage = (
    file: File | string,
    maxWidth: number,
    maxHeight: number,
    quality: number
  ): Promise<{ base64: string; size: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with quality
        const base64 = canvas.toDataURL('image/jpeg', quality / 100);
        const size = Math.round((base64.length * 3) / 4); // Approximate size in bytes
        
        resolve({ base64, size });
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      
      if (typeof file === 'string') {
        img.src = file;
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          img.src = e.target?.result as string;
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
      }
    });
  };

  // Open resize dialog for tile
  const openResizeDialog = (index: number) => {
    const tile = content.tiles[index];
    if (tile?.imageUrl) {
      setResizeTileIndex(index);
      // Calculate original size
      const base64Length = tile.imageUrl.length;
      const sizeBytes = Math.round((base64Length * 3) / 4);
      setOriginalSize(sizeBytes);
      setCompressedSize(0);
      setResizeDialogOpen(true);
    }
  };

  // Preview compression
  const previewCompression = async () => {
    if (resizeTileIndex === null) return;
    
    const tile = content.tiles[resizeTileIndex];
    if (!tile?.imageUrl) return;
    
    try {
      const { size } = await resizeImage(
        tile.imageUrl,
        resizeSettings.maxWidth,
        resizeSettings.maxHeight,
        resizeSettings.quality
      );
      setCompressedSize(size);
    } catch (error) {
      console.error('Error previewing compression:', error);
    }
  };

  // Apply resize to tile image
  const applyResize = async () => {
    if (resizeTileIndex === null) return;
    
    const tile = content.tiles[resizeTileIndex];
    if (!tile?.imageUrl) return;
    
    setResizing(true);
    
    try {
      const { base64 } = await resizeImage(
        tile.imageUrl,
        resizeSettings.maxWidth,
        resizeSettings.maxHeight,
        resizeSettings.quality
      );
      
      handleTileChange(resizeTileIndex, 'imageUrl', base64);
      showSnackbar('Image resized successfully. Click "Save All Changes" to store it.', 'success');
      setResizeDialogOpen(false);
    } catch (error: any) {
      showSnackbar('Failed to resize image: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setResizing(false);
    }
  };

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContent = async () => {
    try {
      const docRef = doc(db, 'homepage_content', 'gallery_section');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setContent(docSnap.data() as GallerySection);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      showSnackbar('Failed to load content', 'error');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'homepage_content', 'gallery_section'), content);
      showSnackbar('Gallery section saved successfully', 'success');
    } catch (error) {
      console.error('Error saving content:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTileChange = (index: number, field: keyof GalleryTile, value: string) => {
    const updatedTiles = [...content.tiles];
    updatedTiles[index] = { ...updatedTiles[index], [field]: value };
    setContent({ ...content, tiles: updatedTiles });
  };

  const handleImageUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadProgress({ ...uploadProgress, [index]: 10 });

    try {
      let base64String: string;
      const fileSizeKB = file.size / 1024;

      // Auto-resize if larger than 1MB
      if (file.size > 1048576) {
        showSnackbar(`Image is ${(fileSizeKB / 1024).toFixed(2)}MB. Auto-compressing...`, 'success');
        setUploadProgress({ ...uploadProgress, [index]: 40 });
        
        const { base64, size } = await resizeImage(file, 1920, 1080, 92);
        base64String = base64;
        
        setUploadProgress({ ...uploadProgress, [index]: 80 });
        const newSizeKB = size / 1024;
        showSnackbar(`Compressed from ${(fileSizeKB / 1024).toFixed(2)}MB to ${(newSizeKB / 1024).toFixed(2)}MB`, 'success');
      } else {
        // File is small enough - just read it
        base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onprogress = (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 80);
              setUploadProgress({ ...uploadProgress, [index]: progress });
            }
          };
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsDataURL(file);
        });
      }

      handleTileChange(index, 'imageUrl', base64String);
      setUploadProgress({ ...uploadProgress, [index]: 100 });
      showSnackbar('Image uploaded successfully', 'success');
      
      // Clear progress after 2 seconds
      setTimeout(() => {
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[index];
          return newProgress;
        });
      }, 2000);
    } catch (error: any) {
      showSnackbar('Failed to upload image: ' + (error.message || 'Unknown error'), 'error');
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[index];
        return newProgress;
      });
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Gallery Section Management
      </Typography>

      {/* Main Section Content */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#d4af37' }}>
          Main Section Details
        </Typography>
        
        <TextField
          fullWidth
          label="Subtitle (Overline)"
          value={content.subtitle}
          onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
          sx={{ mb: 2 }}
          placeholder="GALLERY OVERVIEW"
        />

        <TextField
          fullWidth
          label="Main Title"
          value={content.title}
          onChange={(e) => setContent({ ...content, title: e.target.value })}
          sx={{ mb: 2 }}
          placeholder="Explore Photos & Videos"
        />

        <TextField
          fullWidth
          label="Description"
          value={content.description}
          onChange={(e) => setContent({ ...content, description: e.target.value })}
          multiline
          rows={3}
          sx={{ mb: 2 }}
          placeholder="A glimpse of temple moments — festivals, premises, events..."
        />
      </Paper>

      {/* Gallery Tiles */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#d4af37' }}>
        Gallery Preview Tiles (4 Tiles)
      </Typography>
      
      <Grid container spacing={3}>
        {content.tiles.map((tile, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Paper sx={{ p: 3, border: '2px solid #e0e0e0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#666' }}>
                Tile {index + 1}
              </Typography>
              
              <TextField
                fullWidth
                label="Title"
                value={tile.title}
                onChange={(e) => handleTileChange(index, 'title', e.target.value)}
                sx={{ mb: 2 }}
                placeholder="Photos"
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={tile.type}
                  label="Type"
                  onChange={(e) => handleTileChange(index, 'type', e.target.value as string)}
                >
                  <MenuItem value="photo">Photo</MenuItem>
                  <MenuItem value="video">Video (YouTube)</MenuItem>
                </Select>
              </FormControl>

              {tile.type === 'photo' ? (
                <Box sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Image URL or Path"
                    value={tile.imageUrl || ''}
                    onChange={(e) => handleTileChange(index, 'imageUrl', e.target.value)}
                    sx={{ mb: 2 }}
                    placeholder="/assets/header_god_image.png or base64"
                    helperText="Enter the path or upload an image (max 1MB)"
                  />
                  
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ 
                      mb: 1,
                      borderColor: '#d4af37', 
                      color: '#d4af37',
                      '&:hover': { 
                        borderColor: '#e5c158',
                        bgcolor: 'rgba(212, 175, 55, 0.04)'
                      }
                    }}
                  >
                    Upload Image from Device
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => handleImageUpload(index, e)}
                    />
                  </Button>

                  {uploadProgress[index] !== undefined && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Uploading...
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#d4af37', fontWeight: 700 }}>
                          {uploadProgress[index]}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={uploadProgress[index]} 
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'rgba(212, 175, 55, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: '#d4af37',
                            borderRadius: 3,
                          }
                        }}
                      />
                    </Box>
                  )}

                  {/* Resize Button - Show only if image exists and is base64 */}
                  {tile.imageUrl && tile.imageUrl.startsWith('data:') && (
                    <Button
                      variant="outlined"
                      startIcon={<CompressIcon />}
                      onClick={() => openResizeDialog(index)}
                      fullWidth
                      sx={{ 
                        mt: 1,
                        borderColor: '#2196f3', 
                        color: '#2196f3',
                        '&:hover': { bgcolor: 'rgba(33,150,243,0.1)', borderColor: '#1976d2' }
                      }}
                    >
                      Resize / Compress Image
                    </Button>
                  )}

                  {tile.imageUrl && (
                    <Box sx={{ mt: 2, p: 2, border: '2px dashed #d4af37', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                        Image Preview
                      </Typography>
                      <img 
                        src={tile.imageUrl} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }} 
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="YouTube Video ID"
                  value={tile.youtubeId || ''}
                  onChange={(e) => handleTileChange(index, 'youtubeId', e.target.value)}
                  sx={{ mb: 2 }}
                  placeholder="bFHz3KocQc0"
                  helperText="Enter only the video ID from YouTube URL"
                />
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Chip Label"
                    value={tile.chip}
                    onChange={(e) => handleTileChange(index, 'chip', e.target.value)}
                    placeholder="Curated"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Link"
                    value={tile.link}
                    onChange={(e) => handleTileChange(index, 'link', e.target.value)}
                    placeholder="/gallery/photos"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={loading}
          sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#e5c158' } }}
        >
          {loading ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Resize Dialog */}
      <Dialog 
        open={resizeDialogOpen} 
        onClose={() => setResizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#d4af37', color: '#000', fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompressIcon />
            Resize / Compress Image
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Adjust the settings below to resize and compress your image. Lower quality = smaller file size.
            </Typography>
            
            {originalSize > 0 && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 2, mb: 3 }}>
                <Typography variant="body2">
                  <strong>Current Size:</strong> {(originalSize / 1024).toFixed(2)} KB
                </Typography>
                {compressedSize > 0 && (
                  <Typography variant="body2" sx={{ color: 'green', mt: 0.5 }}>
                    <strong>Estimated New Size:</strong> {(compressedSize / 1024).toFixed(2)} KB 
                    ({Math.round((1 - compressedSize / originalSize) * 100)}% smaller)
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Width: {resizeSettings.maxWidth}px
            </Typography>
            <Slider
              value={resizeSettings.maxWidth}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, maxWidth: value as number }))}
              onChangeCommitted={previewCompression}
              min={320}
              max={1920}
              step={80}
              marks={[
                { value: 320, label: '320' },
                { value: 640, label: '640' },
                { value: 1280, label: '1280' },
                { value: 1920, label: '1920' }
              ]}
              sx={{ color: '#d4af37' }}
            />
          </Box>
          
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Max Height: {resizeSettings.maxHeight}px
            </Typography>
            <Slider
              value={resizeSettings.maxHeight}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, maxHeight: value as number }))}
              onChangeCommitted={previewCompression}
              min={240}
              max={1080}
              step={60}
              marks={[
                { value: 240, label: '240' },
                { value: 480, label: '480' },
                { value: 720, label: '720' },
                { value: 1080, label: '1080' }
              ]}
              sx={{ color: '#d4af37' }}
            />
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Quality: {resizeSettings.quality}%
            </Typography>
            <Slider
              value={resizeSettings.quality}
              onChange={(_, value) => setResizeSettings(prev => ({ ...prev, quality: value as number }))}
              onChangeCommitted={previewCompression}
              min={30}
              max={100}
              step={5}
              marks={[
                { value: 30, label: '30%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              sx={{ color: '#d4af37' }}
            />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Lower quality = smaller file size. 70-80% is recommended for web.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setResizeDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            onClick={previewCompression}
            variant="outlined"
            sx={{ borderColor: '#d4af37', color: '#d4af37' }}
          >
            Preview Size
          </Button>
          <Button 
            onClick={applyResize}
            variant="contained"
            disabled={resizing}
            startIcon={resizing ? <CircularProgress size={16} color="inherit" /> : <CompressIcon />}
            sx={{ bgcolor: '#d4af37', color: '#000', '&:hover': { bgcolor: '#c9a227' } }}
          >
            {resizing ? 'Resizing...' : 'Apply Resize'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GallerySectionManager;
